import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getDeals } from "../lib/api";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Gift, Ship, Building, UtensilsCrossed, Lock, Check, ArrowRight, Star, Users, Clock, Sparkles } from "lucide-react";

const categoryIcons = {
  cruise: Ship,
  hotel: Building,
  restaurant: UtensilsCrossed,
  transport: Ship,
  activity: Gift,
  insurance: Gift,
  other: Gift,
};

const categoryLabels = {
  cruise: "Croisières",
  hotel: "Hôtels",
  restaurant: "Restaurants",
  transport: "Transport",
  activity: "Activités",
  insurance: "Assurance",
  other: "Autre",
  electronics: "Électronique",
  luggage: "Bagagerie",
};

export default function DealsPage() {
  const { isAuthenticated, isMember, user } = useAuth();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const isActiveMember = isMember && user?.membership_status === "active";

  useEffect(() => {
    loadDeals();
  }, []);

  const loadDeals = async () => {
    try {
      const res = await getDeals();
      const dealsData = Array.isArray(res) ? res : (res.data || []);
      setDeals(dealsData);
    } catch (error) {
      console.error("Error loading deals:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter deals based on visibility rules
  // Public sees: teaser_public, actif (with teaser info)
  // Members see: teaser_public, reserve_adherents, actif (full info)
  const visibleDeals = deals.filter(deal => {
    if (!deal.is_published) return false;
    const status = deal.status || "brouillon";
    
    if (isActiveMember) {
      // Members see all visible statuses
      return ["teaser_public", "reserve_adherents", "actif"].includes(status);
    } else {
      // Public only sees teaser_public and actif
      return ["teaser_public", "actif"].includes(status);
    }
  });

  const filteredDeals = activeTab === "all" 
    ? visibleDeals 
    : visibleDeals.filter(d => d.category === activeTab);

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="deals-page">
      {/* Hero */}
      <section className="bg-primary text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl">
            <p className="font-accent text-accent tracking-wide mb-4">Exclusivités</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Bons Plans Association
            </h1>
            <p className="text-white/80 text-lg md:text-xl">
              En tant qu'adhérent, bénéficiez de réductions exclusives négociées 
              auprès de nos partenaires : compagnies de croisières, hôtels de luxe, 
              restaurants gastronomiques...
            </p>
          </div>
        </div>
      </section>

      {/* Member Status Banner */}
      {!isMember && (
        <section className="bg-accent/10 border-y border-accent/20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Lock className="h-5 w-5 text-accent" />
                <p className="text-primary">
                  <span className="font-medium">Les détails des offres sont réservés aux adhérents.</span>
                  {" "}Rejoignez-nous pour en profiter !
                </p>
              </div>
              <Link to={isAuthenticated ? "/espace-membre" : "/adherer"}>
                <Button className="bg-accent hover:bg-accent/90 shrink-0">
                  {isAuthenticated ? "Demander l'adhésion" : "Devenir adhérent"}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Deals List */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <Tabs defaultValue="all" onValueChange={setActiveTab}>
            <TabsList className="mb-8 bg-white">
              <TabsTrigger value="all">Tous</TabsTrigger>
              <TabsTrigger value="cruise">Croisières</TabsTrigger>
              <TabsTrigger value="hotel">Hôtels</TabsTrigger>
              <TabsTrigger value="restaurant">Restaurants</TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab}>
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array(6).fill(0).map((_, i) => (
                    <Skeleton key={i} className="h-64" />
                  ))}
                </div>
              ) : filteredDeals.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredDeals.map((deal) => {
                    const Icon = categoryIcons[deal.category] || Gift;
                    const showFullDetails = isActiveMember || !deal.is_members_only;
                    const isTeaser = !showFullDetails && (deal.status === "teaser_public" || deal.status === "actif");
                    const teaserBenefits = deal.teaser_benefits || [];
                    
                    return (
                      <div 
                        key={deal.id} 
                        className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl ${
                          isTeaser ? "border-accent/30 ring-1 ring-accent/10" : "border-border"
                        }`}
                        data-testid={`deal-card-${deal.id}`}
                      >
                        {/* Badge for members-only offers */}
                        {deal.is_members_only && (
                          <div className={`px-4 py-2 text-xs font-medium flex items-center gap-2 ${
                            isTeaser 
                              ? "bg-gradient-to-r from-accent/10 to-purple-100 text-accent" 
                              : "bg-green-50 text-green-700"
                          }`}>
                            {isTeaser ? (
                              <>
                                <Sparkles className="h-3.5 w-3.5" />
                                Offre exclusive adhérents
                              </>
                            ) : (
                              <>
                                <Check className="h-3.5 w-3.5" />
                                Vous avez accès à cette offre
                              </>
                            )}
                          </div>
                        )}
                        
                        <div className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                                {deal.partner_logo ? (
                                  <img src={deal.partner_logo} alt={deal.partner_name} className="h-8 w-8 object-contain" />
                                ) : (
                                  <Icon className="h-6 w-6 text-accent" />
                                )}
                              </div>
                              <div>
                                <p className="text-sm text-muted-foreground">
                                  {categoryLabels[deal.category]}
                                </p>
                                <p className="font-medium text-primary">{deal.partner_name}</p>
                              </div>
                            </div>
                            {deal.discount && (
                              <span className="text-xl font-bold text-accent bg-accent/10 px-3 py-1 rounded-full">
                                {deal.discount}
                              </span>
                            )}
                          </div>
                          
                          <h3 className="font-heading text-lg font-semibold text-primary mb-3">
                            {deal.title}
                          </h3>
                          
                          {/* Teaser or Full Description */}
                          <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                            {isTeaser && deal.teaser_summary 
                              ? deal.teaser_summary 
                              : deal.description}
                          </p>

                          {/* Teaser Benefits List */}
                          {isTeaser && teaserBenefits.length > 0 && (
                            <div className="mb-4 space-y-2">
                              {teaserBenefits.slice(0, 3).map((benefit, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm">
                                  <Check className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                                  <span className="text-primary">{benefit}</span>
                                </div>
                              ))}
                              {teaserBenefits.length > 3 && (
                                <p className="text-xs text-muted-foreground pl-6">
                                  +{teaserBenefits.length - 3} autres avantages...
                                </p>
                              )}
                            </div>
                          )}

                          {/* Validity dates */}
                          {deal.valid_until && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
                              <Clock className="h-3.5 w-3.5" />
                              <span>Valable jusqu'au {new Date(deal.valid_until).toLocaleDateString('fr-FR')}</span>
                            </div>
                          )}

                          {/* Full details for members OR Teaser CTA */}
                          {showFullDetails ? (
                            <div className="pt-4 border-t border-border space-y-3">
                              {deal.conditions && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Conditions</p>
                                  <p className="text-sm text-primary">{deal.conditions}</p>
                                </div>
                              )}
                              {deal.procedure && (
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground uppercase mb-1">Comment en profiter</p>
                                  <p className="text-sm text-primary">{deal.procedure}</p>
                                </div>
                              )}
                              {deal.code && (
                                <div className="bg-accent/10 rounded-lg p-3 flex items-center justify-between">
                                  <div>
                                    <p className="text-xs font-medium text-accent uppercase">Code promo</p>
                                    <p className="font-mono font-bold text-primary">{deal.code}</p>
                                  </div>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => navigator.clipboard.writeText(deal.code)}
                                  >
                                    Copier
                                  </Button>
                                </div>
                              )}
                              {deal.link_url && (
                                <a 
                                  href={deal.link_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="block"
                                >
                                  <Button className="w-full bg-accent hover:bg-accent/90">
                                    Accéder à l'offre
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                  </Button>
                                </a>
                              )}
                            </div>
                          ) : (
                            <div className="pt-4 border-t border-border">
                              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-4">
                                <Lock className="h-4 w-4" />
                                <span>Détails et conditions réservés aux adhérents</span>
                              </div>
                              <Link to="/adherer">
                                <Button className="w-full bg-accent hover:bg-accent/90 gap-2">
                                  <Users className="h-4 w-4" />
                                  {deal.teaser_cta_text || "Devenir adhérent"}
                                </Button>
                              </Link>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-16">
                  <Gift className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="font-heading text-2xl font-semibold text-primary mb-2">
                    Aucun bon plan pour le moment
                  </h3>
                  <p className="text-muted-foreground">
                    De nouvelles offres arrivent bientôt !
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA for non-members */}
      {!isMember && (
        <section className="bg-primary py-16">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              Accédez à tous les avantages
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              L'adhésion vous donne accès à l'ensemble de nos bons plans négociés 
              et à des contenus exclusifs.
            </p>
            <Link to="/adherer">
              <Button className="bg-accent hover:bg-accent/90 text-white px-8 py-6">
                Rejoindre l'association
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
